import React, { useState, useEffect } from 'react';
import { Lock, Trash2, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react';
import { ProfileService } from '../service/Profile.service';
import { ApiService } from '../../../shared/service/Apiservice';
import axios from 'axios';
import AccountDeletionModal from './AccountDeletionModal';
import './PrivacySettings.css';

const PrivacySettings: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isDataUsageExpanded, setIsDataUsageExpanded] = useState(false);

  const profileService = React.useMemo(() => {
    const axiosInstance = axios.create({
      withCredentials: true,
    });
    const apiService = new ApiService(axiosInstance);
    return new ProfileService(apiService);
  }, []);

  // Fetch deletion status on mount
  useEffect(() => {
    fetchDeletionStatus();
  }, [profileService]);

  const fetchDeletionStatus = async () => {
    try {
      setError(''); // Clear any previous errors
      const response = await profileService.getAccountDeletionStatus();
      console.log('Deletion status response:', response);
      
      if (response.success) {
        setDeletionStatus(response.data);
        console.log('Deletion status fetched:', response.data);
      } else {
        console.error('Failed to fetch deletion status: No success in response', response);
        setDeletionStatus(null);
      }
    } catch (err: any) {
      console.error('Failed to fetch deletion status:', err?.response?.data || err);
      setDeletionStatus(null);
    }
  };

  const handleDeleteAccountClick = () => {
    if (deletionStatus?.status === 'pending' || deletionStatus?.status === 'approved') {
      setError('You already have an active deletion request');
      return;
    }
    setIsModalOpen(true);
  };

  const handleSubmitDeletion = async (reason: string, additionalComments: string) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await profileService.requestAccountDeletion(reason, additionalComments);
      if (response.success) {
        setMessage(response.message || 'Deletion request submitted successfully');
        await fetchDeletionStatus();
      }
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Failed to submit deletion request');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    if (!deletionStatus || deletionStatus.status !== 'pending') {
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await profileService.cancelAccountDeletion();
      if (response.success) {
        setMessage('Deletion request cancelled successfully');
        await fetchDeletionStatus();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to cancel deletion request');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="privacy-settings-container">
      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      {message && (
        <div className="alert alert-success">
          <CheckCircle size={20} />
          <p>{message}</p>
        </div>
      )}

      {/* Data Usage Section - Collapsible */}
      <div className="privacy-section collapsible-section">
        <button
          className={`section-header-button ${isDataUsageExpanded ? 'expanded' : ''}`}
          onClick={() => setIsDataUsageExpanded(!isDataUsageExpanded)}
        >
          <Lock size={24} className="header-lock-icon" />
          <div className="header-content">
            <h2>How We Use Your Data</h2>
            <p className="section-subtitle">Understanding your privacy</p>
          </div>
          <ChevronDown size={24} className="chevron-icon" />
        </button>

        {isDataUsageExpanded && (
          <div className="data-usage-content">
            <div className="usage-item">
              <h3>Account Information</h3>
              <p>
                Your name, email, and phone number are used to create and manage your account, as well as
                to communicate with you about orders and services.
              </p>
            </div>

            <div className="usage-item">
              <h3>Order History</h3>
              <p>
                We maintain records of your purchases to fulfill orders, provide customer support, and
                improve our services. Your order data is used for analytics to better understand customer
                preferences.
              </p>
            </div>

            <div className="usage-item">
              <h3>Payment Information</h3>
              <p>
                Payment details are processed securely and never stored in plain text. We use this
                information to process transactions and prevent fraud.
              </p>
            </div>

            <div className="usage-item">
              <h3>Communication Preferences</h3>
              <p>
                We use your contact information to send order updates, promotions, and important
                announcements. You can manage these preferences in your account settings at any time.
              </p>
            </div>

            <div className="usage-item">
              <h3>Analytics and Improvements</h3>
              <p>
                Aggregated, non-personal data helps us understand usage patterns and improve our platform.
                This information is never sold to third parties.
              </p>
            </div>

            <div className="privacy-policy-link">
              <p>
                For more information, please read our{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  complete Privacy Policy
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Account Deletion Section */}
      <div className="privacy-section account-deletion-section">
        <div className="section-header">
          <Trash2 size={24} />
          <div>
            <h2>Delete Your Account</h2>
            <p className="section-subtitle">Permanently remove your account and data</p>
          </div>
          {deletionStatus?.status === 'pending' || deletionStatus?.status === 'approved' ? (
            <button
              onClick={handleCancelDeletion}
              disabled={isLoading}
              className="btn-cancel-deletion"
            >
              {isLoading ? 'Cancelling...' : 'Cancel'}
            </button>
          ) : (
            <button
              onClick={handleDeleteAccountClick}
              disabled={isLoading}
              className="btn-delete-account"
            >
              {isLoading ? 'Processing...' : 'Delete'}
            </button>
          )}
        </div>

        {deletionStatus?.status === 'pending' || deletionStatus?.status === 'approved' ? (
          // Deletion Request Pending
          <div className="deletion-pending">
            <div className="status-box">
              <div className="status-icon pending">
                <AlertCircle size={32} />
              </div>
              <div className="status-content">
                <h3>Deletion Request Pending</h3>
                <p>
                  Your account deletion request was submitted on{' '}
                  <strong>{formatDate(deletionStatus.requestDate)}</strong>
                </p>
                <p>
                  Your account will be permanently deleted on{' '}
                  <strong>{formatDate(deletionStatus.scheduledDeletionDate)}</strong>
                </p>
                <p className="status-description">
                  During this 30-day period, you can cancel the deletion request and continue using
                  your account. After the scheduled date, all your data will be permanently and
                  irrevocably deleted.
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Deletion Modal */}
      <AccountDeletionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmitDeletion}
        isLoading={isLoading}
      />
    </div>
  );
};

export default PrivacySettings;
